import './user-dropdown.scss';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import OutsideClickHandler from '@/components/Shared/ui/outside-click-handler';
import { getUserInitials } from '@/services/helpers/strings';
import { useAppSelector } from '@/store';

interface MenuItem {
  key: string;
  label: string;
  onClick: () => void;
}

interface UserDropdownProps {
  children: React.ReactNode;
  items: MenuItem[];
}

const UserDropdown: React.FC<UserDropdownProps> = ({ children, items }) => {
  const { t } = useTranslation();
  const { clientAdditionalInfo } = useAppSelector((state) => state.client);

  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleItemClick = (onClick: () => void) => {
    onClick();
    handleClose();
  };

  return (
    <OutsideClickHandler onOutsideClick={handleClose}>
      <div className="user-dropdown">
        <div
          role="button"
          tabIndex={0}
          aria-label={t('a11y.user-menu')}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggle();
            }
          }}
        >
          {children}
        </div>
        {isOpen && (
          <div className="user-dropdown__menu">
            {clientAdditionalInfo.initialValues && (
              <div className="user-dropdown__header">
                <span className="user-dropdown__header-text">
                  {getUserInitials(clientAdditionalInfo.initialValues)}
                </span>
              </div>
            )}
            <ul className="user-dropdown__list" role="menu">
              {items.map((item) => (
                <li
                  key={item.key}
                  className="user-dropdown__item"
                  role="menuitem"
                  tabIndex={0}
                  onClick={() => handleItemClick(item.onClick)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleItemClick(item.onClick);
                    }
                  }}
                >
                  {item.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default UserDropdown;
